// IP合作「出价方案」历史轮次对比 diff 类型与契约
// 与设计稿图例对齐：新增(绿)/编辑(橙)/删除(灰)；判定规则为纯数据对比（用户已确认）

/** diff 类型：added=历史无/最新有；edited=都有但值不同；deleted=历史有/最新无 */
export type RoundDiffType = 'added' | 'edited' | 'deleted';

/** 单个单元格/字段的 diff 值（newValue=最新轮展示值；oldValue=历史轮删除线值） */
export interface RoundDiffValue {
  type: RoundDiffType;
  /** 历史轮次值（added 时为空；deleted 时为主要展示） */
  oldValue?: unknown;
  /** 最新轮次值（deleted 时为空；其余为主要展示） */
  newValue?: unknown;
}

/** diff 类型展示文案（图例标签） */
export const ROUND_DIFF_TEXT: Record<RoundDiffType, string> = {
  added: '新增',
  edited: '编辑',
  deleted: '删除',
};

/** diff 类型 → bedrock Tag color（回复列 action diff 渲染 Tag 用；色系语义对齐 DIFF_TEXT_CLASS：新增绿/编辑橙/删除灰） */
export const ROUND_DIFF_TAG_COLOR: Record<
  RoundDiffType,
  'light-green' | 'light-orange' | 'light-grey'
> = {
  added: 'light-green',
  edited: 'light-orange',
  deleted: 'light-grey',
};

/** 核心商务条款 diff（key=item_key：$estimated_amount / payment_terms / license_regions） */
export type CoreTermDiffMap = Record<string, RoundDiffValue>;

/** 核心商务条款 回复/意见 diff（key=item_key；版权方回复与意见反馈列对比用） */
export interface CoreTermItemDiff {
  /** 版权方回复（action：ACCEPT/MODIFY/REJECT）diff */
  action?: RoundDiffValue;
  /** 意见反馈/说明（opinion 文本）diff */
  opinion?: RoundDiffValue;
}
export type CoreTermItemDiffMap = Record<string, CoreTermItemDiff>;

/** 费用预算 diff（key=费用预算字段：total_amount/lf_amount/mg_amount/royalty_type/royalty_rate/lf_enabled/mg_enabled/royalty_enabled） */
export type BudgetDiffMap = Record<string, RoundDiffValue>;

/** IP合作授权信息（联动周期）单行 diff（key=index_id） */
export interface SolutionRowDiff {
  /** 整行类型（added=最新轮新增行 / deleted=历史轮删除行；缺省=存在但字段有变化） */
  rowType?: RoundDiffType;
  /** 授权时长（license_duration，跨行合并列） */
  authDuration?: RoundDiffValue;
  /** 联动上线时长 */
  durationValue?: RoundDiffValue;
  /** 联动上线周期 */
  onlinePeriod?: RoundDiffValue;
  /** 联动下线周期 */
  offlinePeriod?: RoundDiffValue;
  /** 版权方回复 diff（SOLUTION 条款，跨行合并列） */
  action?: RoundDiffValue;
  /** 意见反馈/说明 diff（SOLUTION 条款，跨行合并列） */
  opinion?: RoundDiffValue;
}
export type SolutionDiffMap = Record<string, SolutionRowDiff>;

/** 权益 单分组 diff（key=group_value/benefit_code） */
export interface BenefitRowDiff {
  /** 整行类型（分组新增/删除；缺省=存在但 attr 有变化） */
  rowType?: RoundDiffType;
  /** 是否开启变化 */
  enabled?: RoundDiffValue;
  /** 各 attribute diff（key=attr_code） */
  attrs?: Record<string, RoundDiffValue>;
  /** 版权方回复 diff（BENEFIT 条款，key=benefit_code） */
  action?: RoundDiffValue;
  /** 意见反馈/说明 diff（BENEFIT 条款） */
  opinion?: RoundDiffValue;
}
export type BenefitDiffMap = Record<string, BenefitRowDiff>;

/** 付款计划 单行 diff（key=合并数组下标 i${index}；roundDiff.ts 按节点 md5 分组 + 组内子行 index 生成；payment_type_key 为每次渲染重新生成的 uuid，不作 diff 键） */
export interface PaymentPlanRowDiff {
  /** 整行类型（added=最新轮新增行 / deleted=历史轮删除行；缺省=存在但字段有变化） */
  rowType?: RoundDiffType;
  /** 付款节点（复合值：节点类型 + 时间；节点 md5 分组匹配下内容必然一致，当前不生成该字段 diff，预留兼容） */
  paymentNode?: RoundDiffValue;
  /** 付款类型（LF/MG/Royalty） */
  paymentType?: RoundDiffValue;
  /** 付款金额（金额或分成占比） */
  paymentAmount?: RoundDiffValue;
  /** 版权方回复 diff（付款计划条款，跨行合并列） */
  action?: RoundDiffValue;
  /** 意见反馈/说明 diff（付款计划条款，跨行合并列） */
  opinion?: RoundDiffValue;
}
export type PaymentPlanDiffMap = Record<string, PaymentPlanRowDiff>;

/** 三区块整体 diff 结果 */
export interface RoundSectionDiff {
  /** 核心商务条款（固定 3 行，行集不变，仅单元格 diff） */
  coreTerms?: CoreTermDiffMap;
  /** 核心商务条款 回复/意见列 diff（key=item_key） */
  coreTermItems?: CoreTermItemDiffMap;
  /** IP合作授权信息：rows=合并后展示行（历史+最新轮并集）；diff 按 index_id 索引 */
  solution?: { rows: any[]; diff: SolutionDiffMap };
  /** 权益：rows=合并后展示行；diff 按 group_value 索引 */
  benefits?: { rows: any[]; diff: BenefitDiffMap };
  /** 费用预算（预算总金额/授权金/保底金/分成各字段 diff，key=费用预算字段） */
  budget?: BudgetDiffMap;
  /** 费用预算 回复/意见列 diff（key=预算条款 item_key） */
  budgetItems?: CoreTermItemDiffMap;
  /** 付款计划：rows=合并后展示行（历史+最新轮并集）；diff 按渲染行下标 index 索引 */
  paymentPlan?: { rows: any[]; diff: PaymentPlanDiffMap };
}
